import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Car, Plus, Trash } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useConfirmDialog } from '../components/ConfirmDialog';
import MyPremiumBookingsCard from '../components/MyPremiumBookingsCard';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyCarPage() {
  const { t } = useLanguage();
  const { ConfirmDialog, confirm } = useConfirmDialog();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ license_plate: '', make: '', model: '', color: '' });
  const [adding, setAdding] = useState(false);

  const fetchCars = async () => {
    try {
      const res = await axios.get(`${API}/cars`, { withCredentials: true });
      setCars(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCars();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAddCar = async (e) => {
    e.preventDefault();
    if (!form.license_plate.trim()) {
      toast.error(t('plateRequired') || 'License plate is required');
      return;
    }

    setAdding(true);
    try {
      await axios.post(`${API}/cars`, form, { withCredentials: true });
      toast.success(t('success'));
      setShowDialog(false);
      setForm({ license_plate: '', make: '', model: '', color: '' });
      fetchCars();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add car');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteCar = async (carId, plate) => {
    const ok = await confirm({
      title: t('removeVehicleTitle') || 'Remove vehicle?',
      description: `${t('removeVehicleDesc') || 'This will remove'} ${plate}.`,
      confirmText: t('remove') || 'Remove',
      cancelText: t('cancel') || 'Cancel',
      danger: true,
    });
    if (!ok) return;

    try {
      await axios.delete(`${API}/cars/${carId}`, { withCredentials: true });
      toast.success(t('vehicleRemoved') || 'Vehicle removed');
      fetchCars();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove car');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] safe-bottom flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0ABAB5] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] safe-bottom">
      {/* Header */}
      <div className="p-4 pt-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-tight font-['Outfit']">{t('myCars')}</h1>
          <p className="text-white/50 mt-1">Register your vehicles</p>
        </div>
        <Button
          onClick={() => setShowDialog(true)}
          className="h-12 px-4 bg-[#0ABAB5] hover:bg-[#0ABAB5]/90 text-black font-semibold rounded-2xl"
          data-testid="add-car-btn"
        >
          <Plus size={20} weight="bold" className="mr-2" />
          {t('addCar')}
        </Button>
      </div>

      {/* Iter26 D — Active premium-booking confirmation card(s) */}
      <MyPremiumBookingsCard />

      {/* Cars List */}
      <div className="px-4 pb-8">
        {cars.length === 0 ? (
          <motion.div
            className="flex flex-col items-center justify-center py-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-4">
              <Car size={40} weight="duotone" className="text-white/20" />
            </div>
            <p className="text-white/50">{t('noCars')}</p>
            <Button
              onClick={() => setShowDialog(true)}
              className="mt-4 h-12 px-6 bg-[#0ABAB5] hover:bg-[#0ABAB5]/90 text-black font-semibold rounded-2xl"
            >
              <Plus size={20} weight="bold" className="mr-2" />
              {t('addCar')}
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {cars.map((car, index) => (
              <motion.div
                key={car.car_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-4 rounded-2xl bg-[#121212] border border-white/5"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#0ABAB5]/10 flex items-center justify-center">
                      <Car size={24} weight="duotone" className="text-[#0ABAB5]" />
                    </div>
                    <div>
                      <p className="font-mono text-lg font-semibold tracking-wider">{car.license_plate}</p>
                      {car.make && (
                        <p className="text-white/50 text-sm">{car.make}</p>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteCar(car.car_id, car.license_plate)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid={`delete-car-${car.car_id}`}
                  >
                    <Trash size={20} />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Add Car Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit']">{t('addCar')}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleAddCar} className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-white/60 mb-2 block">{t('licensePlate')} *</label>
              <Input
                name="license_plate"
                placeholder="AA 123 BB"
                value={form.license_plate}
                onChange={handleChange}
                className="h-14 bg-[#0a0a0a] border-white/10 rounded-xl text-white font-mono uppercase placeholder:text-white/30 focus:border-[#0ABAB5]"
                data-testid="car-plate-input"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-white/60 mb-2 block">{t('make')}</label>
                <Input
                  name="make"
                  placeholder="Mercedes"
                  value={form.make}
                  onChange={handleChange}
                  className="h-12 bg-[#0a0a0a] border-white/10 rounded-xl text-white placeholder:text-white/30 focus:border-[#0ABAB5]"
                  data-testid="car-make-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={adding}
              className="w-full h-14 bg-[#0ABAB5] hover:bg-[#0ABAB5]/90 text-black font-semibold rounded-2xl glow-primary mt-2"
              data-testid="save-car-btn"
            >
              {adding ? t('loading') : t('save')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </div>
  );
}
